import { useEffect, useState, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { LiliAsyncAutoComplete } from '@lili/lili-frontend-common';
import { t } from 'i18next';
import { OnboardingFormFields } from '@Types/index';
import { getStateDropdownItem } from '@Components/inputs/AddressStateDropDown';
import { useAddressAutocomplete } from '@Hooks/api/useAddressAutocomplete';
import { useBoundStore } from '@Store/index';
import type { SmartySuggestion } from '@Services/smarty/smarty.service';
import type { Suggestion } from '@lili/lili-frontend-common';

interface Props {
  disabled?: boolean;
  tabIndex?: number;
  validStates?: { title: string; value: string }[];
}

const toSuggestion = (s: SmartySuggestion) => {
  const parts = [s.street_line, s.secondary].filter(Boolean).join(' ');
  const location = `${s.city}, ${s.state} ${s.zipcode}`;
  const title =
    s.entries > 1 ? `${parts}, ${location} (${s.entries} units)` : `${parts}, ${location}`;
  const value = `${parts} ${s.city} ${s.state} ${s.zipcode}`;
  return { title, value, data: s };
};

export const Line1AutocompleteInput = ({ disabled, tabIndex, validStates }: Props) => {
  const {
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const { field: line1Field } = useController({ name: OnboardingFormFields.LINE1 });
  const { suggestions, isLoading, search, drillDown } = useAddressAutocomplete();
  const [selected, setSelected] = useState<Suggestion | null>(null);

  const line1Value = line1Field.value as string | undefined;

  // Clear selected state when form value is externally reset (e.g. resetField(''))
  useEffect(() => {
    if (!line1Value || disabled) {
      setSelected(null);
    }
  }, [line1Value, disabled]);

  const handleSearch = (query: string) => {
    if (!query || query.length < 3) return;
    search(query);
  };

  const getSelectDisplayTitle = (suggestion: Suggestion) => {
    const s = suggestion.data as SmartySuggestion;
    return s?.street_line ?? suggestion.title;
  };

  const fillForm = (s: SmartySuggestion, suggestion: Suggestion) => {
    setSelected({ ...suggestion, title: s.street_line });
    setValue(OnboardingFormFields.LINE1, s.street_line, { shouldValidate: true });
    setValue(OnboardingFormFields.APARTMENT, s.secondary ?? '', { shouldValidate: true });
    setValue(OnboardingFormFields.CITY, s.city, { shouldValidate: true });
    setValue(OnboardingFormFields.STATE, getStateDropdownItem(s.state, null, validStates), {
      shouldValidate: true,
    });
    setValue(OnboardingFormFields.ZIP, s.zipcode, { shouldValidate: true });
  };

  const handleSelect = (suggestion: Suggestion) => {
    const s = suggestion.data as SmartySuggestion;

    if (s.entries > 1) {
      drillDown(s);
      return;
    }

    fillForm(s, suggestion);
  };

  const handleClear = () => {
    setSelected(null);
    setValue(OnboardingFormFields.LINE1, '');
  };

  const { setFormFieldError } = useBoundStore((state) => state.errorSlice);

  const handleInputChange = (value: string) => {
    setFormFieldError(OnboardingFormFields.LINE1, '');
    setValue(OnboardingFormFields.LINE1, value, { shouldValidate: true });
  };

  const handleBlur = () => {
    trigger(OnboardingFormFields.LINE1);
  };

  const errorMessage = errors[OnboardingFormFields.LINE1]?.message as string | undefined;

  const mappedSuggestions = useMemo(() => suggestions.map(toSuggestion), [suggestions]);

  return (
    <LiliAsyncAutoComplete
      placeholder={t('pages.homeAddress.lineOneFieldTitle')}
      label={t('pages.homeAddress.lineOneFieldTitle')}
      suggestions={mappedSuggestions}
      selectedSuggestion={selected}
      isLoading={isLoading}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onClear={handleClear}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      getSelectDisplayTitle={getSelectDisplayTitle}
      displayValue={line1Value ?? ''}
      debounceTime={300}
      disabled={disabled}
      tabIndex={tabIndex}
      inputTestId={OnboardingFormFields.LINE1}
      isPublic
      errorMessage={errorMessage}
      errorTestId={`${OnboardingFormFields.LINE1}-error-container`}
    />
  );
};
